import { useMemo, useState } from "react";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Wifi, Wind, Thermometer, Tv, ChefHat, Refrigerator, Microwave, Coffee, Wine,
  Bath, ShowerHead, Sparkles, Sofa, Building2, MapPin, Trees, PawPrint, Baby,
  Cigarette, Utensils, Clock, ConciergeBell, Car, Waves, Dumbbell, Snowflake,
  Warehouse, Accessibility, LampFloor, Plus, X, ShieldCheck
} from "lucide-react";

// Predefined amenities with bilingual labels + icon.
// Value is the stored string (Italian, since that's the primary language).
export const AMENITY_CATALOG = [
  {
    key: "base",
    label_it: "Essenziali", label_en: "Essentials",
    items: [
      { v: "Wi-Fi", it: "Wi-Fi", en: "Wi-Fi", icon: Wifi },
      { v: "Aria condizionata", it: "Aria condizionata", en: "Air conditioning", icon: Snowflake },
      { v: "Riscaldamento", it: "Riscaldamento", en: "Heating", icon: Thermometer },
      { v: "Bagno privato", it: "Bagno privato", en: "Private bathroom", icon: Bath },
      { v: "Set biancheria", it: "Set biancheria", en: "Linens provided", icon: Sparkles },
      { v: "TV", it: "TV", en: "TV", icon: Tv },
      { v: "Asciugacapelli", it: "Asciugacapelli", en: "Hairdryer", icon: Wind },
    ],
  },
  {
    key: "cucina",
    label_it: "Cucina", label_en: "Kitchen",
    items: [
      { v: "Cucina attrezzata", it: "Cucina attrezzata", en: "Full kitchen", icon: ChefHat },
      { v: "Frigorifero", it: "Frigorifero", en: "Fridge", icon: Refrigerator },
      { v: "Microonde", it: "Microonde", en: "Microwave", icon: Microwave },
      { v: "Macchina caffè", it: "Macchina caffè", en: "Coffee machine", icon: Coffee },
      { v: "Bollitore", it: "Bollitore", en: "Kettle", icon: Utensils },
      { v: "Lavastoviglie", it: "Lavastoviglie", en: "Dishwasher", icon: Utensils },
    ],
  },
  {
    key: "comfort",
    label_it: "Comfort", label_en: "Comfort",
    items: [
      { v: "Smart TV", it: "Smart TV", en: "Smart TV", icon: Tv },
      { v: "Netflix", it: "Netflix / streaming", en: "Netflix / streaming", icon: Tv },
      { v: "Cassaforte", it: "Cassaforte", en: "Safe", icon: ShieldCheck },
      { v: "Ferro da stiro", it: "Ferro da stiro", en: "Iron", icon: LampFloor },
      { v: "Balcone", it: "Balcone", en: "Balcony", icon: Building2 },
      { v: "Terrazza", it: "Terrazza", en: "Terrace", icon: Sofa },
      { v: "Vista mare", it: "Vista mare", en: "Sea view", icon: Waves },
      { v: "Vista montagna", it: "Vista montagna", en: "Mountain view", icon: MapPin },
      { v: "Vista città", it: "Vista città", en: "City view", icon: Building2 },
    ],
  },
  {
    key: "bagno",
    label_it: "Bagno", label_en: "Bathroom",
    items: [
      { v: "Doccia", it: "Doccia", en: "Shower", icon: ShowerHead },
      { v: "Vasca", it: "Vasca da bagno", en: "Bathtub", icon: Bath },
      { v: "Bidet", it: "Bidet", en: "Bidet", icon: Bath },
      { v: "Prodotti bagno", it: "Prodotti bagno", en: "Toiletries", icon: Sparkles },
      { v: "Accappatoio", it: "Accappatoio", en: "Bathrobe", icon: Sparkles },
    ],
  },
  {
    key: "servizi",
    label_it: "Servizi", label_en: "Services",
    items: [
      { v: "Parcheggio gratuito", it: "Parcheggio gratuito", en: "Free parking", icon: Car },
      { v: "Garage", it: "Garage", en: "Garage", icon: Warehouse },
      { v: "Ascensore", it: "Ascensore", en: "Elevator", icon: Building2 },
      { v: "Accesso disabili", it: "Accesso disabili", en: "Wheelchair access", icon: Accessibility },
      { v: "Piscina", it: "Piscina", en: "Pool", icon: Waves },
      { v: "Palestra", it: "Palestra", en: "Gym", icon: Dumbbell },
      { v: "Giardino", it: "Giardino", en: "Garden", icon: Trees },
      { v: "Reception", it: "Reception", en: "Reception", icon: ConciergeBell },
      { v: "Check-in 24h", it: "Check-in 24h", en: "24h check-in", icon: Clock },
    ],
  },
  {
    key: "extra",
    label_it: "Policy & Extra", label_en: "Policy & Extras",
    items: [
      { v: "Animali ammessi", it: "Animali ammessi", en: "Pets allowed", icon: PawPrint },
      { v: "Culla disponibile", it: "Culla disponibile", en: "Crib available", icon: Baby },
      { v: "Fumatori esterno", it: "Fumatori (esterno)", en: "Smoking (outdoor)", icon: Cigarette },
      { v: "Colazione inclusa", it: "Colazione inclusa", en: "Breakfast included", icon: Coffee },
      { v: "Aperitivo di benvenuto", it: "Aperitivo di benvenuto", en: "Welcome drink", icon: Wine },
    ],
  },
];

// Flat lookup by stored value
const ALL_ITEMS = AMENITY_CATALOG.flatMap(g => g.items);
const KNOWN_VALUES = new Set(ALL_ITEMS.map(i => i.v));

export default function AmenitiesPicker({ value = [], onChange }) {
  const { lang } = useLang();
  const IT = lang === "it";
  const selected = useMemo(() => new Set(value || []), [value]);
  const customs = useMemo(() => (value || []).filter(v => !KNOWN_VALUES.has(v)), [value]);
  const [customInput, setCustomInput] = useState("");

  const toggle = (v) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v); else next.add(v);
    onChange(Array.from(next));
  };

  const addCustom = () => {
    const c = customInput.trim();
    if (!c) return;
    if (selected.has(c)) { setCustomInput(""); return; }
    onChange([...(value || []), c]);
    setCustomInput("");
  };

  const removeCustom = (v) => {
    onChange((value || []).filter(x => x !== v));
  };

  return (
    <div className="space-y-6" data-testid="amenities-picker">
      <div className="flex items-center justify-between">
        <div>
          <div className="uppercase tracking-[0.2em] text-[10px] text-sage font-semibold">
            {IT ? "Dotazioni della struttura" : "Property amenities"}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {IT
              ? `${selected.size} selezionate · appariranno in vetrina e nella conferma di prenotazione`
              : `${selected.size} selected · will appear on the showcase and in booking confirmation`}
          </p>
        </div>
        {selected.size > 0 && (
          <Button variant="ghost" size="sm" onClick={() => onChange([])}
            className="text-xs text-muted-foreground hover:text-destructive"
            data-testid="amenities-clear-all">
            {IT ? "Deseleziona tutto" : "Clear all"}
          </Button>
        )}
      </div>

      {AMENITY_CATALOG.map((group) => (
        <div key={group.key}>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">
            {IT ? group.label_it : group.label_en}
          </div>
          <div className="flex flex-wrap gap-2">
            {group.items.map((item) => {
              const Icon = item.icon;
              const isSel = selected.has(item.v);
              return (
                <button
                  key={item.v}
                  type="button"
                  onClick={() => toggle(item.v)}
                  className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-all ${
                    isSel
                      ? "bg-primary/10 border-primary text-primary font-medium shadow-sm"
                      : "bg-card border-border text-muted-foreground hover:border-primary/40 hover:text-foreground"
                  }`}
                  data-testid={`amenity-${item.v.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <Icon className="w-3.5 h-3.5"/>
                  <span>{IT ? item.it : item.en}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {/* Custom amenities */}
      <div>
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">
          {IT ? "Altre dotazioni personalizzate" : "Custom amenities"}
        </div>
        <div className="flex gap-2">
          <Input
            value={customInput}
            onChange={(e) => setCustomInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }}
            placeholder={IT ? "Es. Barbecue, Sauna finlandese, Idromassaggio…" : "e.g. BBQ, Finnish sauna, Jacuzzi…"}
            data-testid="amenity-custom-input"
            className="flex-1"
          />
          <Button type="button" onClick={addCustom} variant="outline" data-testid="amenity-custom-add">
            <Plus className="w-4 h-4 mr-1"/>{IT ? "Aggiungi" : "Add"}
          </Button>
        </div>
        {customs.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {customs.map(c => (
              <span key={c} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-sage/10 border border-sage/40 text-sage" data-testid={`amenity-custom-${c.replace(/\s+/g, "-").toLowerCase()}`}>
                {c}
                <button type="button" onClick={() => removeCustom(c)} className="hover:text-destructive">
                  <X className="w-3 h-3"/>
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
